import { useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import get from "lodash/get";

const findCabinByCodeOrLabel = (cabins, value) =>
	cabins.find(({ code }) => code === value) ?? cabins.find(({ label }) => label === value);

// Mount this only while a cabin fallback is active (caller's job). Corrects via setFieldValue
// (not initialValues, to avoid resetting the whole form) once per mount, so it never overrides
// a deliberate cabin re-selection by the user.
const CabinFallbackCorrection = ({ cabins, cabinFallback }) => {
	const { values, setFieldValue } = useFormikContext();
	const cabinCode = get(values, "cabin.code");
	const hasRunRef = useRef(false);

	// useLayoutEffect: corrects before paint to avoid flashing the unavailable cabin.
	useLayoutEffect(() => {
		// Pas de cabine = valeurs provisoires du premier rendu, que Formik remplace après le
		// montage (enableReinitialize) : on garde l'unique correction pour les vraies valeurs.
		if (hasRunRef.current || !cabinCode) {
			return;
		}

		hasRunRef.current = true;

		// The backend's fallback text isn't guaranteed to be the cabin `code` — some brands send
		// the display label instead — so check both before deciding nothing needs correcting.
		const currentCabin = cabins.find(({ code }) => code === cabinCode);
		const cabinCausedTheFallback =
			cabinCode === cabinFallback.cabinRequested ||
			currentCabin?.label === cabinFallback.cabinRequested;

		if (!cabinCausedTheFallback) {
			return;
		}

		const correctedCabin = findCabinByCodeOrLabel(cabins, cabinFallback.cabinReturned);

		if (!correctedCabin) {
			return;
		}

		setFieldValue("cabin", correctedCabin);
	}, [cabins, cabinCode, cabinFallback, setFieldValue]);

	return null;
};

CabinFallbackCorrection.propTypes = {
	cabins: PropTypes.array.isRequired,
	cabinFallback: PropTypes.object.isRequired,
};

export default CabinFallbackCorrection;
